import {ClaimReport, ClaimReportResponse} from "../interfaces/claim-report";
import {HttpClient} from "@angular/common/http";
import {inject, Injectable, signal} from "@angular/core";
import {map, Observable, tap, shareReplay, catchError, of, EMPTY} from "rxjs";
import {environment} from "../../environments/environment";
import {toObservable} from "@angular/core/rxjs-interop";
import {UserType} from "../enums/user-type";

const endpoint = environment.backendApi + "/api/claim/";


@Injectable({
  providedIn: 'root'
})
export class ClaimsService {
  private http = inject(HttpClient);

  #hasDataFetched = signal(false);
  private dataRequest$: Observable<ClaimReport> | null = null;
  private requestFailed = false;
  private claimReportInitialValue = {
    policeReportImageUrls: [],
    policeReportAvailable: null,
    policeReportNotAvailableReason: null,
    externalsInvolved: null,
    claimType: null,
    incidentAddress: {street: null, houseNumber: null, city: null, postcode: null},
    incidentDate: null,
    licensePlate: null,
    vehicleType: null,
    userType: null,
    involvedCarParts: [],
    incidentTypes: [],
    detailedDescription: null,
    rideReason: null,
    age: null,
    hasConsumedDrugs: null,
    policeReportWasIssued: null,
    noPoliceReportReason: null,
    policeDrugTestPerformed: null,
    witnessesAreKnown: null,
    witnesses: [],
    technicalIssue: null,
    technicalIssueExplanation: null,
    fault: null,
    thirdPartyEntities: [],
    fileUploads: null,
    personalInformation: {
      contactableByEmail: null,
      phone: null,
      city: null,
      zip: null,
      name: null,
      email: null,
      streetAndNumber: null,
      contactableBySms: null,
      surname: null
    },
    userHasConfirmedCorrectnessOfData: null,
    isVerified: null,
    accidentRoleType: null,
    isActiveRider: null,
    vehicleIncidentStatusType: null,
  };
  #claimReport = signal<ClaimReport>(this.claimReportInitialValue
  )
  claimReport = this.#claimReport.asReadonly();
  hasDataFetched = this.#hasDataFetched.asReadonly();
  claimReport$: Observable<ClaimReport>;

  constructor() {
    this.claimReport$ = toObservable(this.claimReport);
  }

  resetClaimReport() {
    this.#claimReport.set(this.claimReportInitialValue);
    this.#hasDataFetched.set(false);
    this.dataRequest$ = null;
    this.requestFailed = false;
  }

  getData() {
    if (this.hasDataFetched()) {
      return this.claimReport$;
    }

    // If request already failed, return empty observable to prevent repeated failures
    if (this.requestFailed) {
      return EMPTY;
    }

    if (this.dataRequest$) {
      return this.dataRequest$;
    }

    this.dataRequest$ = this.http.get<ClaimReport>(endpoint).pipe(
      tap(data => {
        this.#claimReport.set(data);
        this.#hasDataFetched.set(true);
        this.requestFailed = false;
      }),
      catchError(error => {
        console.warn('Claims service request failed:', error.status === 403 ? 'Authentication required' : error.statusText);
        this.dataRequest$ = null;
        this.requestFailed = true; // Mark as failed to prevent retry
        return EMPTY;
      }),
      shareReplay(1)
    );

    return this.dataRequest$;
  }

  save() {
    return this.http.post<ClaimReportResponse>(endpoint, this.claimReport())
      .pipe(
        map(response => response.data),
        tap(data => this.#claimReport.set(data)));
  }

  isDriver(): boolean {
    return this.claimReport().userType === UserType.FirstParty;
  }

  isDamagedPerson(): boolean {
    return this.claimReport().userType === UserType.ThirdParty;
  }

  updateClaimReport(claimReport: Partial<ClaimReport>) {
    this.#claimReport.update(claim => ({...claim, ...claimReport}));
  }
}
